import { Fragment, useEffect, useState } from "react";
import { PlusCircle, Lock } from "lucide-react";
import { http, toastApiError, getUserLabels } from "../../lib/api";
import { toast } from "../../lib/notify";
import { useAuth } from "../../context/AuthContext";
import SgkPeriodCard from "../../components/sgk/SgkPeriodCard";
import SgkPeriodForm from "../../components/sgk/SgkPeriodForm";
import { EMPTY_PERIOD, toFormRates } from "../../components/sgk/sgkConst";

// SGK Prim Oranları — dönem kartları (en yeni üstte); "Yeni Oranlar Gir" seçili kartın hemen altında mevcut oranlarla dolu form açar
export default function SgkRates() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [periods, setPeriods] = useState([]);
  const [userLabels, setUserLabels] = useState({});
  const [editor, setEditor] = useState(null);  // { mode: "new"|"edit", anchor: period|null }

  const load = () => http.get("/sgk-rate-periods").then((r) => setPeriods(r.data)).catch(toastApiError);
  useEffect(() => { load(); getUserLabels().then(setUserLabels); }, []);

  const save = async (payload) => {
    try {
      if (editor.mode === "edit") { await http.put(`/sgk-rate-periods/${editor.anchor.id}`, payload); toast.success("Dönem güncellendi"); }
      else { await http.post("/sgk-rate-periods", payload); toast.success("Yeni dönem kaydedildi — önceki dönemin son geçerlilik tarihi otomatik güncellendi"); }
      setEditor(null); load();
    } catch (e) { toastApiError(e); }
  };
  const remove = async (p) => {
    if (!window.confirm(`${p.valid_from} tarihli SGK prim dönemi silinecek. Emin misiniz?`)) return;
    try { await http.delete(`/sgk-rate-periods/${p.id}`); toast.success("Dönem silindi"); load(); } catch (e) { toastApiError(e); }
  };
  const formFor = (anchor) => {
    const isEdit = editor.mode === "edit";
    const init = isEdit ? { valid_from: anchor.valid_from, valid_to: anchor.valid_to || "", notes: anchor.notes || "" } : { ...EMPTY_PERIOD };
    return <SgkPeriodForm key={`${editor.mode}-${anchor?.id || "top"}`} initial={init} rates={toFormRates(anchor?.rates)} mode={editor.mode} onSave={save} onCancel={() => setEditor(null)} prefix="sgk" />;
  };

  return (
    <div className="space-y-4" data-testid="sgk-rates">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-slate-600" data-testid="sgk-period-count">{periods.length} dönem · en yeni üstte · Son geçerlilik tarihi yeni dönem girilince otomatik dolar</span>
        {!canEdit && <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-0.5" data-testid="sgk-readonly"><Lock className="w-3 h-3" /> Düzenleme yalnız Yönetici</span>}
        {canEdit && periods.length === 0 && !editor && <button type="button" onClick={() => setEditor({ mode: "new", anchor: null })} data-testid="sgk-new-top" className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"><PlusCircle className="w-3.5 h-3.5" /> İlk Dönemi Gir</button>}
      </div>
      {editor && !editor.anchor && formFor(null)}
      {periods.map((p) => (
        <Fragment key={p.id}>
          {editor?.mode === "edit" && editor.anchor?.id === p.id ? formFor(p) : <SgkPeriodCard p={p} canEdit={canEdit} userLabels={userLabels} onNewBelow={(x) => setEditor({ mode: "new", anchor: x })} onEdit={(x) => setEditor({ mode: "edit", anchor: x })} onRemove={remove} />}
          {editor?.mode === "new" && editor.anchor?.id === p.id && formFor(p)}
        </Fragment>
      ))}
      {periods.length === 0 && !editor && <div className="text-sm text-slate-400 bg-white border border-slate-200 rounded-lg px-4 py-8 text-center" data-testid="sgk-empty">Kayıt yok</div>}
    </div>
  );
}
